/**
 * Filter utility — cache cinematic filter definitions and resolve CSS for a filter ID.
 */
import { api } from './api';

let _cache = null;
let _fetching = false;
let _waiters = [];

async function ensureLoaded() {
  if (_cache) return _cache;
  if (_fetching) {
    return new Promise(resolve => _waiters.push(resolve));
  }
  _fetching = true;
  try {
    const res = await api.getCinematicFilters();
    _cache = res.data?.filters || [];
  } catch {
    _cache = [];
  }
  _fetching = false;
  _waiters.forEach(fn => fn(_cache));
  _waiters = [];
  return _cache;
}

/**
 * Get the CSS filter + overlay for a given filter_applied ID.
 * Returns { cssFilter, overlay } or null if unknown / original.
 */
export async function resolveFilterCSS(filterId, intensity = 100) {
  if (!filterId || filterId === 'original') return null;
  const filters = await ensureLoaded();
  const f = filters.find(x => x.id === filterId);
  if (!f || f.css_filter === 'none') return null;

  // Scale filter intensity
  const scale = (intensity ?? 100) / 100;
  let css = f.css_filter;
  if (scale !== 1) {
    css = css.replace(/(\d+)%/g, (_, n) => `${Math.round(Number(n) * scale)}%`);
  }
  return { cssFilter: css, overlay: f.overlay };
}

/**
 * Synchronous version — returns from cache only (call ensureLoaded first).
 */
export function resolveFilterCSSSync(filterId, intensity = 100) {
  if (!filterId || filterId === 'original' || !_cache) return null;
  const f = _cache.find(x => x.id === filterId);
  if (!f || f.css_filter === 'none') return null;
  const scale = (intensity ?? 100) / 100;
  let css = f.css_filter;
  if (scale !== 1) {
    css = css.replace(/(\d+)%/g, (_, n) => `${Math.round(Number(n) * scale)}%`);
  }
  return { cssFilter: css, overlay: f.overlay };
}

/** Pre-warm the cache (call once from App or layout). */
export function preloadFilters() {
  ensureLoaded();
}
